import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import type {
  TransferReportDateField,
  TransferTrendGroupBy,
} from "@/modules/reports/domain/repo/transfer/transfer-report-filters";
import {
  formatDateForInput,
  parseCalendarDateFromInput,
} from "@/shared/lib/date-time";
import { DatePicker } from "@/shared/ui/DatePicker";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  TRANSFER_FILTER_BODY,
  TRANSFER_FILTER_CARD,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useIngredientCategoriesLookupQuery } from "@/modules/inventory/presentation/api/ingredient-categories-queries";
import { XIcon } from "lucide-react";
import { useMemo } from "react";

const FILTER_ALL = "__all__" as const;

export type TransferReportsSearch = {
  from?: string;
  to?: string;
  dateField?: TransferReportDateField;
  groupBy?: TransferTrendGroupBy;
  fromBranchId?: string;
  ingredientCategoryId?: string;
  offset?: number;
  limit?: number;
};

const DATE_FIELD_OPTIONS: { value: TransferReportDateField; label: string }[] =
  [
    { value: "submitted", label: "ວັນທີສົ່ງຄຳຂໍ" },
    { value: "shipped", label: "ວັນທີຈັດສົ່ງ" },
    { value: "received", label: "ວັນທີຮັບ" },
    { value: "closed", label: "ວັນທີປິດງານ" },
  ];

export function TransferReportsFilter() {
  const nav = useNavigate({ from: "/app/reports/inventory/transfers" });
  const search = useSearch({
    from: "/app/reports/inventory/transfers",
  }) as TransferReportsSearch;

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });

  const branches: BranchRow[] = branchesData?.data ?? [];
  const branchOptions = [
    { value: FILTER_ALL, label: "ທຸກສາຂາຂໍ" },
    ...branches
      .filter((b) => !b.isDistributionHub)
      .map((b) => ({
        value: b.id,
        label: `${b.code} — ${b.name}`,
      })),
  ];

  const categoriesLookup = useIngredientCategoriesLookupQuery();
  const categoryOptions = useMemo(
    () => [
      { value: FILTER_ALL, label: "ທຸກຫມວດວັດຖຸດິບ" },
      { value: "__uncategorized__", label: "ຍັງບໍ່ຈັດຫມວດ" },
      ...(categoriesLookup.data ?? []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    ],
    [categoriesLookup.data],
  );

  const hasFilter =
    !!search.fromBranchId ||
    !!search.ingredientCategoryId ||
    search.dateField !== "submitted" ||
    !!search.from ||
    !!search.to;

  const update = (next: Partial<TransferReportsSearch>) => {
    nav({ search: { ...search, ...next, offset: 0 } });
  };

  return (
    <Card className={TRANSFER_FILTER_CARD}>
      <CardContent className={TRANSFER_FILTER_BODY}>
      <SimpleSelect
        placeholder="ນັບຕາມ"
        value={search.dateField ?? "submitted"}
        onValueChange={(v) =>
          update({ dateField: (v as TransferReportDateField) ?? "submitted" })
        }
        options={DATE_FIELD_OPTIONS}
        className="h-9 w-44"
      />
      <DatePicker
        mode="range"
        value={{
          from: search.from
            ? parseCalendarDateFromInput(search.from)
            : undefined,
          to: search.to ? parseCalendarDateFromInput(search.to) : undefined,
        }}
        onChange={(range) => {
          const from = range?.from;
          const to = range?.to;
          update({
            from: from ? formatDateForInput(from) : undefined,
            to: to ? formatDateForInput(to) : undefined,
          });
        }}
        placeholder="ຊ່ວງວັນທີ"
        className="h-9 w-fit min-w-56"
      />
      <SimpleSelect
        placeholder="ສາຂາຂໍ"
        value={search.fromBranchId ?? FILTER_ALL}
        onValueChange={(v) => {
          const id = v === FILTER_ALL || !v ? undefined : v;
          update({ fromBranchId: id });
        }}
        options={branchOptions}
        className="h-9 min-w-[12rem]"
      />
      <SimpleSelect
        placeholder="ຫມວດວັດຖຸດິບ"
        value={search.ingredientCategoryId ?? FILTER_ALL}
        onValueChange={(v) => {
          const id = v === FILTER_ALL || !v ? undefined : v;
          update({ ingredientCategoryId: id });
        }}
        options={categoryOptions}
        className="h-9 min-w-[11rem]"
      />
      {hasFilter ? (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
          onClick={() =>
            nav({
              search: {
                limit: search.limit ?? 50,
                offset: 0,
              },
            })
          }
        >
          <XIcon className="size-4" />
          ລ້າງຕົວກອງ
        </button>
      ) : null}
      </CardContent>
    </Card>
  );
}
